import React from 'react';
import {Alert, Box, CircularProgress, Stack, Typography} from '@mui/material';
import ReadonlyProseMirrorRenderer from './ReadonlyProseMirrorRenderer';
import DualLanguagePanes from './DualLanguagePanes';
import SourcesFileProcessingProgress from './SourcesFileProcessingProgress';
import SourceFileDraftSelectionHost from './SourceFileDraftSelectionHost';

/**
 * Image split-pane layout — mirrors the audio/video transcript layout:
 *   ┌──────────────────────┐
 *   │  Original image (top)│  sticky strip (drag the divider to resize)
 *   ├──────────────────────┤  ← draggable divider
 *   │  Caption + OCR text  │  scrollable (LLM-extracted)
 *   └──────────────────────┘
 *
 * The original upload is streamed through dash-api (/raw/stream with the
 * authenticated userId query param) so the picture renders even though the
 * PM document's own image node points at an un-authenticated relative URL.
 * The extracted-text pane reuses the readonly PM renderer but strips any
 * top-level image node so the original is not duplicated below as a broken img.
 */

const MIN_TOP_PX = 80;
const MIN_BOTTOM_PX = 60;
const DEFAULT_TOP_PERCENT = 50;

/** Returns a shallow copy of the PM doc with top-level image nodes removed. */
function stripTopLevelImages(pmContent) {
    if (!pmContent || pmContent.type !== 'doc' || !Array.isArray(pmContent.content)) {
        return pmContent;
    }
    return {
        ...pmContent,
        content: pmContent.content.filter((node) => node?.type !== 'image'),
    };
}

/** True when the doc has at least one non-image block worth rendering as text. */
function hasTextBlocks(pmContent) {
    if (!pmContent || pmContent.type !== 'doc' || !Array.isArray(pmContent.content)) {
        return false;
    }
    return pmContent.content.some((node) => node?.type && node.type !== 'image');
}

/**
 * Returns the current USABLE height of a referenced container element.
 *
 * We cap at `window.innerHeight` because the viewer is an absolutely-positioned
 * overlay whose containing block can be as tall as the full document scroll height.
 * Without the cap the image panel would fill half the document (e.g. 3000 px) and
 * push the caption/text panel completely off-screen.
 */
function useContainerHeight(ref) {
    const [height, setHeight] = React.useState(0);
    React.useEffect(() => {
        if (!ref.current) return;
        const measure = () => {
            if (!ref.current) return;
            const elH = ref.current.getBoundingClientRect().height;
            setHeight(Math.min(elH, window.innerHeight));
        };
        const ro = new ResizeObserver(measure);
        ro.observe(ref.current);
        measure();
        return () => ro.disconnect();
    }, [ref]);
    return height;
}

const SourcesImageLayout = ({
    src,
    alt,
    pmContent,
    originalPmContent = null,
    originalLanguageLabel = 'Original',
    originalDir = 'ltr',
    isLoadingText,
    textError,
    processingStatus,
    onBlockHighlight = null,
    highlightedSegmentIds = null,
    highlightedRanges = null,
    linkContext = null,
    searchMatches = null,
    activeSearchMatchIdx = -1,
    draftContext = null,
}) => {
    const [imageError, setImageError] = React.useState(false);
    const [topPercent, setTopPercent] = React.useState(DEFAULT_TOP_PERCENT);
    const containerRef = React.useRef(null);
    const isDragging = React.useRef(false);
    const containerHeight = useContainerHeight(containerRef);

    React.useEffect(() => {
        setImageError(false);
    }, [src]);

    const textDoc = React.useMemo(() => stripTopLevelImages(pmContent), [pmContent]);
    const originalTextDoc = React.useMemo(
        () => (originalPmContent ? stripTopLevelImages(originalPmContent) : null),
        [originalPmContent],
    );

    // Map each RENDERED (stripped) block index back to its CANONICAL block index. stripTopLevelImages
    // drops top-level image nodes, so the renderer's data-block-index differs from the canonical
    // numberInItem the create API keys on. Without this, creating a highlight stores the wrong block.
    const renderedToCanonical = React.useMemo(() => {
        const children = pmContent?.type === 'doc' && Array.isArray(pmContent.content) ? pmContent.content : null;
        if (!children) return null;
        const map = [];
        children.forEach((node, canonicalIndex) => {
            if (node?.type === 'image') return; // stripped — consumes no rendered index
            map.push(canonicalIndex);
        });
        return map;
    }, [pmContent]);

    // Wrap the highlight-create callback to translate the rendered block index (and keep offsets)
    // to the canonical index before it reaches the parent's segment lookup.
    const handleBlockHighlight = React.useMemo(() => {
        if (typeof onBlockHighlight !== 'function') return null;
        return (arg) => {
            const sel = typeof arg === 'object' && arg != null ? arg : {blockIndex: arg};
            const canonical = renderedToCanonical ? renderedToCanonical[sel.blockIndex] : sel.blockIndex;
            onBlockHighlight({...sel, blockIndex: canonical == null ? sel.blockIndex : canonical});
        };
    }, [onBlockHighlight, renderedToCanonical]);

    // Highlights key on the block index within the FULL canonical doc, but stripTopLevelImages
    // removes top-level image nodes — shifting indices. Remap each highlighted index to its
    // position in the stripped (rendered) doc so the right text block gets marked.
    const remappedHighlightIds = React.useMemo(() => {
        if (!Array.isArray(highlightedSegmentIds) || highlightedSegmentIds.length === 0) return null;
        const children = pmContent?.type === 'doc' && Array.isArray(pmContent.content) ? pmContent.content : null;
        if (!children) return highlightedSegmentIds;
        // For each original index, count how many top-level image nodes precede it.
        const set = new Set(highlightedSegmentIds.map(Number));
        const out = [];
        let renderedIndex = -1;
        children.forEach((node, i) => {
            if (node?.type === 'image') return; // stripped — not rendered, no index consumed
            renderedIndex += 1;
            if (set.has(i)) out.push(renderedIndex);
        });
        return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [highlightedSegmentIds?.join(','), pmContent]);

    // Remap TEXT_RANGE offsets keyed by canonical block index to the stripped doc's indices,
    // mirroring remappedHighlightIds. Char offsets are unaffected by image stripping (per block).
    const remappedHighlightRanges = React.useMemo(() => {
        if (!highlightedRanges || typeof highlightedRanges !== 'object') return null;
        const keys = Object.keys(highlightedRanges);
        if (keys.length === 0) return null;
        const children = pmContent?.type === 'doc' && Array.isArray(pmContent.content) ? pmContent.content : null;
        if (!children) return highlightedRanges;
        const out = {};
        let renderedIndex = -1;
        children.forEach((node, i) => {
            if (node?.type === 'image') return;
            renderedIndex += 1;
            if (highlightedRanges[i]) out[renderedIndex] = highlightedRanges[i];
        });
        return Object.keys(out).length ? out : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [highlightedRanges, pmContent]);
    // Remap searchMatches blockIndices from canonical (original) to rendered (stripped) indices.
    // stripTopLevelImages removes top-level image nodes, shifting all subsequent block indices.
    const remappedSearchMatches = React.useMemo(() => {
        if (!Array.isArray(searchMatches) || searchMatches.length === 0) return searchMatches;
        const children = pmContent?.type === 'doc' && Array.isArray(pmContent.content) ? pmContent.content : null;
        if (!children) return searchMatches;
        // Build canonical → rendered index map.
        const canonicalToRendered = {};
        let renderedIndex = -1;
        children.forEach((node, i) => {
            if (node?.type === 'image') return; // stripped — not counted in rendered doc
            renderedIndex += 1;
            canonicalToRendered[i] = renderedIndex;
        });
        // Remap each match; drop any that land on a stripped image block (no rendered index).
        return searchMatches
            .map((m) => {
                const rendered = canonicalToRendered[m.blockIndex];
                return rendered != null ? {...m, blockIndex: rendered} : null;
            })
            .filter(Boolean);
    }, [searchMatches, pmContent]);

    const showText = hasTextBlocks(pmContent);
    const showOriginalText = hasTextBlocks(originalPmContent);

    const layoutDraftContext = React.useMemo(() => {
        if (!draftContext) return null;
        return {
            ...draftContext,
            toCanonicalBlockIndex: (renderedIndex) => (
                renderedToCanonical?.[renderedIndex] ?? renderedIndex
            ),
        };
    }, [draftContext, renderedToCanonical]);

    const topPx = containerHeight > 0
        ? Math.max(MIN_TOP_PX, Math.min(containerHeight - MIN_BOTTOM_PX, (topPercent / 100) * containerHeight))
        : null;

    const handleDividerMouseDown = React.useCallback((e) => {
        e.preventDefault();
        isDragging.current = true;
        document.body.style.cursor = 'row-resize';
        document.body.style.userSelect = 'none';

        const onMouseMove = (moveEvent) => {
            if (!isDragging.current || !containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const rawPx = moveEvent.clientY - rect.top;
            const clampedPx = Math.max(MIN_TOP_PX, Math.min(rect.height - MIN_BOTTOM_PX, rawPx));
            setTopPercent((clampedPx / rect.height) * 100);
        };

        const onMouseUp = () => {
            isDragging.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    }, []);

    return (
        <Stack ref={containerRef} sx={{flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative'}}>
            {/* Top panel — image */}
            <Box
                sx={(theme) => ({
                    flexShrink: 0,
                    bgcolor: theme.palette.background.default,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    ...(topPx !== null ? {height: topPx} : {flex: '0 0 50%'}),
                })}
            >
                {src && !imageError ? (
                    <Box
                        component="img"
                        src={src}
                        alt={alt || 'Uploaded image'}
                        onError={() => setImageError(true)}
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            borderRadius: 1,
                            display: 'block',
                        }}
                    />
                ) : (
                    <Typography variant="caption" color="text.secondary">
                        {imageError ? 'Unable to load the original image.' : 'No image available.'}
                    </Typography>
                )}
            </Box>

            {/* Draggable divider */}
            <Box
                onMouseDown={handleDividerMouseDown}
                sx={(theme) => ({
                    flexShrink: 0,
                    height: 5,
                    cursor: 'row-resize',
                    bgcolor: theme.palette.divider,
                    position: 'relative',
                    zIndex: 10,
                    transition: 'background-color 0.15s',
                    '&:hover': {
                        bgcolor: theme.palette.primary.main,
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 32,
                        height: 3,
                        borderRadius: 4,
                        bgcolor: 'rgba(255,255,255,0.55)',
                        pointerEvents: 'none',
                    },
                })}
            />

            {/* Bottom panel — caption / OCR text */}
            <Box sx={{flex: 1, minHeight: 0, overflowY: showOriginalText ? 'hidden' : 'auto'}}>
                {processingStatus ? (
                    <SourcesFileProcessingProgress status={processingStatus}/>
                ) : isLoadingText ? (
                    <Stack alignItems="center" justifyContent="center" spacing={2} sx={{p: 4}}>
                        <CircularProgress size={28}/>
                        <Typography variant="body2" color="text.secondary">Loading description…</Typography>
                    </Stack>
                ) : textError ? (
                    <Alert severity="error" sx={{m: 2}}>{textError}</Alert>
                ) : showText ? (
                    <SourceFileDraftSelectionHost
                        draftContext={layoutDraftContext}
                        sx={showOriginalText ? {flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'} : null}
                    >
                        {showOriginalText ? (
                            <DualLanguagePanes
                                leftLabel="English"
                                rightLabel={originalLanguageLabel}
                                left={<Box sx={{p: 3, pr: 5}}><ReadonlyProseMirrorRenderer content={textDoc} onBlockHighlight={handleBlockHighlight} highlightedSegmentIds={remappedHighlightIds} highlightedRanges={remappedHighlightRanges} linkContext={linkContext} searchMatches={remappedSearchMatches} activeSearchMatchIdx={activeSearchMatchIdx}/></Box>}
                                right={<Box sx={{p: 3}}><ReadonlyProseMirrorRenderer content={originalTextDoc} dir={originalDir}/></Box>}
                            />
                        ) : (
                            <Box sx={{p: 3, pr: 5}}>
                                <ReadonlyProseMirrorRenderer content={textDoc} onBlockHighlight={handleBlockHighlight} highlightedSegmentIds={remappedHighlightIds} highlightedRanges={remappedHighlightRanges} linkContext={linkContext} searchMatches={remappedSearchMatches} activeSearchMatchIdx={activeSearchMatchIdx}/>
                            </Box>
                        )}
                    </SourceFileDraftSelectionHost>
                ) : (
                    <Stack alignItems="center" justifyContent="center" sx={{p: 4}}>
                        <Typography variant="body2" color="text.secondary">
                            No description extracted from this image.
                        </Typography>
                    </Stack>
                )}
            </Box>
        </Stack>
    );
};

export default SourcesImageLayout;
